import { useState, useEffect, useRef } from 'react'
import { CommentCard } from './CommentCard'
import { ImageModal } from './ImageModal'
import { DateRangeFilter } from './DateRangeFilter'
import { useComments } from '../hooks/useComments'
import type { CommentImage, Comment, Image } from '../api/types'

/**
 * コメントグリッド
 *
 * コメント一覧をグリッド表示する。
 * 各コメントには複数の画像がサムネイル表示される。
 */
export function CommentGrid() {
  const [filters, setFilters] = useState<{
    startDate?: string
    endDate?: string
  }>({})
  const [selectedImage, setSelectedImage] = useState<{
    images: CommentImage[]
    initialIndex: number
    comment: Comment
  } | null>(null)

  const { data, loading, error, loadMore } = useComments(filters)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // 無限スクロール: Intersection Observerで「さらに読み込む」ボタン付近を監視
  useEffect(() => {
    if (!loadMoreRef.current || !data?.nextCursor || loading) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // ボタンが画面に表示されたら自動的に読み込む
        if (entries[0]?.isIntersecting) {
          loadMore()
        }
      },
      {
        // ボタンが画面下から100px手前に来たら発火
        rootMargin: '100px',
      }
    )

    observer.observe(loadMoreRef.current)

    return () => {
      observer.disconnect()
    }
  }, [data?.nextCursor, loading, loadMore])

  const handleFilterChange = (startDate?: string, endDate?: string) => {
    setFilters({ startDate, endDate })
  }

  const handleImageClick = (comment: Comment, image: CommentImage) => {
    const imageIndex = comment.images.findIndex((img) => img.key === image.key)
    setSelectedImage({
      images: comment.images,
      initialIndex: imageIndex >= 0 ? imageIndex : 0,
      comment,
    })
  }

  const handleCloseModal = () => {
    setSelectedImage(null)
  }

  // CommentImageとCommentをImageModalが期待するImage型に変換
  const convertToImageType = (
    image: CommentImage,
    comment: Comment
  ): Image => ({
    key: image.key,
    url: image.url,
    date: comment.date,
    notes: comment.notes,
    memberName: comment.memberName,
  })

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">
          コメントの読み込みに失敗しました: {error.message}
        </p>
        {error.message === 'Unauthorized' && (
          <p className="error-hint">
            セッションが切れました。再度ログインしてください。
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="comment-grid-container">
      <DateRangeFilter onFilterChange={handleFilterChange} />

      {loading && !data && (
        <div className="loading-container">
          <p>コメントを読み込み中...</p>
        </div>
      )}

      {data && data.comments.length === 0 && (
        <div className="empty-container">
          <p>コメントが見つかりません</p>
          <p className="empty-hint">日付範囲を調整してみてください</p>
        </div>
      )}

      {data && data.comments.length > 0 && (
        <>
          <div className="comment-grid">
            {data.comments.map((comment) => (
              <CommentCard
                key={comment.timelineId}
                comment={comment}
                onClick={(image) => handleImageClick(comment, image)}
              />
            ))}
          </div>

          {data.nextCursor && !loading && (
            <div ref={loadMoreRef} className="load-more-container">
              <button onClick={loadMore} className="load-more-button">
                さらに読み込む
              </button>
            </div>
          )}

          {loading && (
            <div className="loading-more">
              <p>読み込み中...</p>
            </div>
          )}
        </>
      )}

      {selectedImage && (
        <ImageModal
          images={selectedImage.images.map((img) =>
            convertToImageType(img, selectedImage.comment)
          )}
          initialIndex={selectedImage.initialIndex}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
